/** @format */

import {useState, useCallback, useMemo, useEffect, useRef} from 'react';

import {useNavigate, useParams} from 'react-router-dom';
import i18n from 'i18next';

import {ROUTES} from 'constants/ROUTES';

import {
  useGetChatsQuery,
  useGetChatQuery,
  useCreateChatAsyncMutation,
  useSendChatMessageAsyncMutation,
  useRenameChatMutation,
  useDeleteChatMutation,
} from '../graphql/__generated__';

const POLL_INTERVAL_MS = 2500;
// Клиентский failsafe: если backend завис и никогда не проставит COMPLETED/FAILED,
// прекращаем бесконечный поллинг и разблокируем ввод. Заведомо больше нормального
// времени ответа модели (~2 мин).
const POLL_TIMEOUT_MS = 300000;

export const getErrorMessage = (err: unknown): string =>
  (err instanceof Error && err.message) || i18n.t('Произошла ошибка. Попробуйте ещё раз.');

export const useChatData = () => {
  const navigate = useNavigate();
  const {companyId = '', chatId} = useParams<{
    companyId: string;
    chatId: string;
  }>();

  const [sendError, setSendError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState(0);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [sendToken, setSendToken] = useState(0);

  const {
    data: chatsData,
    loading: chatsLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useGetChatsQuery({
    variables: {companyId: companyId || ''},
    skip: !companyId,
  });

  const {
    data: chatData,
    loading: chatLoading,
    error: chatError,
    refetch: refetchChat,
  } = useGetChatQuery({
    variables: {companyId: companyId || '', chatId: chatId || ''},
    skip: !companyId || !chatId,
    pollInterval,
  });

  const [createChatAsync] = useCreateChatAsyncMutation();
  const [sendChatMessageAsync] = useSendChatMessageAsyncMutation();
  const [renameChat] = useRenameChatMutation();
  const [deleteChat] = useDeleteChatMutation();

  const rawChats = chatsData?.currentUser?.company?.aiAgentChats ?? [];
  const chats = useMemo(
    () => [...rawChats].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')),
    [rawChats],
  );
  const companyName = chatsData?.currentUser?.company?.name ?? '';

  // Устойчивость к транзиентным сбоям поллинга: глобальный fetchPolicy 'no-cache'
  // (+ errorPolicy 'none') обнуляет chatData на ЛЮБОЙ упавший poll. Держим последний
  // успешный снимок текущего чата, чтобы один сбойный poll не стирал переписку и не
  // ронял isPending (иначе pollInterval уходит в 0 и поллинг встаёт навсегда).
  // Сбрасываем кэш при смене чата, чтобы не показать сообщения другого чата.
  const rawCurrentChat = chatData?.currentUser?.company?.aiAgentChat ?? null;
  const lastGoodChatRef = useRef<typeof rawCurrentChat>(null);
  const lastChatIdRef = useRef(chatId);
  // id только что созданного чата: handleSendMessage взводит awaitingReply перед
  // navigate() в новый чат, и этот единственный переход chatId сбрасывать НЕЛЬЗЯ.
  const justCreatedChatIdRef = useRef<string | null>(null);
  if (lastChatIdRef.current !== chatId) {
    lastChatIdRef.current = chatId;
    lastGoodChatRef.current = null;
    // Сброс «эпизода ожидания» на любой смене чата (adjust-state-during-render,
    // без flicker в отличие от useEffect). Единый механизм вместо сброса в каждом
    // хендлере-навигаторе: покрывает и смены мимо них — browser back/forward,
    // прямой URL, удаление текущего чата. Исключение — переход в свежесозданный
    // чат: send уже взвёл awaitingReply перед navigate, иначе поллинг нового чата
    // не стартует. Гард `lastChatIdRef.current !== chatId` уже обновлён выше →
    // блок не перевзводится на re-render и безопасен к StrictMode double-invoke.
    if (chatId && chatId === justCreatedChatIdRef.current) {
      justCreatedChatIdRef.current = null;
    } else {
      if (awaitingReply) setAwaitingReply(false);
      if (pollTimedOut) setPollTimedOut(false);
    }
  }
  if (rawCurrentChat) {
    lastGoodChatRef.current = rawCurrentChat;
  }
  const currentChat = rawCurrentChat ?? lastGoodChatRef.current;
  const messages = currentChat?.messages ?? [];

  // Backend сериализует до одного PENDING-ассистента на чат, поэтому find даёт
  // стабильный id между поллингами — failsafe-таймер (ниже) на него и опирается.
  const pendingMessageId =
    messages.find(m => m.role === 'ASSISTANT' && m.status === 'PENDING')?.id ?? null;

  // После успешной отправки ассистент гарантированно PENDING на сервере, но
  // увидеть его мешает возможный сбой post-send refetch. awaitingReply держит
  // поллинг включённым, пока свежий ответ сервера не подтвердит состояние (тогда
  // дальше рулит pendingMessageId). Иначе упавший refetch → PENDING не виден →
  // поллинг не стартует → сообщение и ответ не появятся до перезагрузки.
  // Снимаем флаг на первом же успешном ответе сервера (rawCurrentChat != null).
  useEffect(() => {
    if (rawCurrentChat) {
      setAwaitingReply(false);
    }
  }, [rawCurrentChat]);

  const awaiting = pendingMessageId !== null || awaitingReply;
  // awaitingKey меняется на каждом новом «эпизоде ожидания»: новая отправка (sendToken),
  // смена identity pending-сообщения, начало/конец ожидания. Failsafe на него и опирается,
  // поэтому таймер корректно перевзводится и сбрасывает pollTimedOut для каждого эпизода.
  // 'sent' покрывает окно, когда отправка прошла, но pending ещё не виден — иначе при
  // постоянно падающем GET_CHAT таймер бы не взвёлся и isPending застрял бы true (C1).
  const awaitingKey = awaiting ? `${pendingMessageId ?? 'sent'}#${sendToken}` : null;

  useEffect(() => {
    if (!awaitingKey) {
      setPollTimedOut(false);
      return;
    }
    setPollTimedOut(false);
    const id = setTimeout(() => {
      setPollTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [awaitingKey]);

  const isPending = awaiting && !pollTimedOut;

  // Синхронизация pollInterval во время рендера (паттерн React "adjust state during
  // render", без useEffect): isPending выводится из chatData — результата этого же
  // useGetChatQuery, поэтому не передаётся в его pollInterval напрямую (self-ref).
  // setState здесь синхронно перезапускает рендер до коммита; Apollo реагирует на
  // смену опции немедленно (useResubscribeIfNecessary).
  const nextPollInterval = isPending ? POLL_INTERVAL_MS : 0;
  if (pollInterval !== nextPollInterval) {
    setPollInterval(nextPollInterval);
  }

  // По завершении поллинга (pending true -> false) обновить список чатов.
  // Тот же переход срабатывает при переключении с ещё pending чата на не pending —
  // лишний refetchChats() безвреден. Ошибку глушим (устаревший список не критичен).
  const prevPendingRef = useRef(false);
  useEffect(() => {
    if (prevPendingRef.current && !isPending) {
      refetchChats().catch(() => {});
    }
    prevPendingRef.current = isPending;
  }, [isPending, refetchChats]);

  const chatsBasePath = `/${ROUTES.HOME}/${companyId}/chats`;

  // awaitingReply/pollTimedOut сбрасывает render-phase-блок по смене chatId (см.
  // выше) — единый механизм на все навигации, поэтому хендлеры их не трогают.
  const handleSelectChat = useCallback(
    (id: string) => {
      setSendError(null);
      navigate(`${chatsBasePath}/${id}`);
    },
    [navigate, chatsBasePath],
  );

  const handleNewChat = useCallback(() => {
    setSendError(null);
    navigate(chatsBasePath);
  }, [navigate, chatsBasePath]);

  const handleSendMessage = useCallback(
    async (message: string): Promise<boolean> => {
      if (!message.trim() || !companyId) return false;

      setSendError(null);

      try {
        if (!chatId) {
          const result = await createChatAsync({
            variables: {companyId, message},
          });
          const created = result.data?.createChatAsync;
          if (!created || !created.id) {
            setSendError(i18n.t('Не удалось создать чат. Попробуйте ещё раз.'));
            return false;
          }
          // Симметрично existing-chat пути: держим поллинг включённым до первого
          // успешного GET_CHAT нового чата, иначе сбойный первый fetch оставляет
          // pollInterval на 0 и поллинг никогда не стартует (нет пути к retry).
          setAwaitingReply(true);
          setSendToken(t => t + 1);
          // Метим переход в новый чат, чтобы render-phase-сброс его пропустил.
          justCreatedChatIdRef.current = created.id;
          navigate(`${chatsBasePath}/${created.id}`);
          // Sidebar обновит новый ход; ошибку глушим, отправка уже прошла.
          void refetchChats().catch(() => {});
          return true;
        }

        await sendChatMessageAsync({variables: {chatId, message}});
        // Пользователь мог уйти из чата (переключение/удаление), пока mutation
        // летела. render-phase reset уже отработал по смене chatId — не взводим
        // ожидание и не рефетчим в чужом роуте, иначе destination (особенно
        // пустой /chats, где GET_CHAT скипается) залипнет в thinking до 5-мин
        // failsafe. Отправка всё равно прошла (return true).
        if (lastChatIdRef.current !== chatId) {
          return true;
        }
        setAwaitingReply(true);
        setSendToken(t => t + 1);
      } catch (err) {
        setSendError(getErrorMessage(err));
        if (chatId) {
          try {
            await refetchChat();
          } catch {
            // best-effort resync (ловит и серверную гонку); не роняем обработчик
          }
        }
        return false;
      }

      // Успешная отправка в существующий чат: обновить сообщения и список чатов,
      // не превращая транзиентный сбой обновления в ложную ошибку отправки. Даже
      // если refetch упадёт, awaitingReply удержит поллинг до догона состояния.
      try {
        await Promise.all([refetchChat(), refetchChats()]);
      } catch {
        // устаревшие данные допустимы; awaitingReply-поллинг их догонит
      }
      return true;
    },
    [
      chatId,
      companyId,
      createChatAsync,
      sendChatMessageAsync,
      navigate,
      chatsBasePath,
      refetchChats,
      refetchChat,
    ],
  );

  const handleRenameChat = useCallback(
    async (id: string, title: string) => {
      if (!title.trim()) return;

      try {
        await renameChat({variables: {chatId: id, title}});
        await refetchChats();
      } catch (err) {
        setSendError(getErrorMessage(err));
      }
    },
    [renameChat, refetchChats],
  );

  const handleDeleteChat = useCallback(
    async (id: string) => {
      try {
        await deleteChat({variables: {chatId: id}});
        await refetchChats();

        if (id === chatId) {
          navigate(chatsBasePath);
        }
      } catch (err) {
        setSendError(getErrorMessage(err));
      }
    },
    [deleteChat, chatId, navigate, chatsBasePath, refetchChats],
  );

  const clearSendError = useCallback(() => {
    setSendError(null);
  }, []);

  return {
    companyId,
    companyName,
    chatId: chatId || null,
    chats,
    messages,
    currentChat,

    chatsLoading,
    chatLoading,
    isPending,
    pollTimedOut,

    chatsError: chatsError || null,
    // Не показывать ошибку загрузки, если есть что показать (транзиентный сбой
    // поллинга при наличии last-good-снимка) — иначе переписка сменится на Alert.
    chatError: currentChat ? null : chatError || null,
    sendError,

    handleSelectChat,
    handleNewChat,
    handleSendMessage,
    handleRenameChat,
    handleDeleteChat,
    clearSendError,
  };
};
